import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ArrowUpRight, AlertTriangle, FileSearch, TrendingDown, Layers, Skull, ScanLine, Crosshair } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <Navbar />

      {/* HERO */}
      <section className="relative border-b-2 border-strong overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/33435450/pexels-photo-33435450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 py-20 lg:py-32 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2 h-2 bg-[#FF2A00] blink" />
              <span className="text-xs uppercase tracking-[0.3em] text-zinc-600">Live · Forensic Engine v1</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] mb-8">
              READ BETWEEN<br />
              THE <span className="text-[#FF2A00]">LINES.</span>
            </h1>
            <p className="text-base sm:text-lg max-w-2xl text-zinc-700 leading-relaxed mb-10">
              FinLens dissects annual & quarterly reports line by line — from auditor remarks to footnotes —
              and surfaces the red flags that companies hope you'll never read.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/auth?mode=signup"
                data-testid="hero-get-started"
                className="group bg-black text-white px-7 py-4 text-sm uppercase tracking-[0.2em] inline-flex items-center gap-3 hover:bg-[#002FA7] transition-colors shadow-brutal">
                Start Free Analysis <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/auth" className="px-7 py-4 text-sm uppercase tracking-[0.2em] border-2 border-strong hover:bg-black hover:text-white transition-colors" data-testid="hero-login">
                Sign In
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
              <span>● PDF Upload</span>
              <span>● Ticker Fetch</span>
              <span>● Red Flag Detection</span>
              <span>● Forensic AI Analysis</span>
            </div>
          </div>

          <div className="lg:col-span-4 bg-white border-2 border-strong p-6 self-start shadow-brutal-blue">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Sample Red Flag</div>
            <div className="border-l-4 border-[#FF2A00] bg-[#FEF2F2] p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-[#FF2A00]" strokeWidth={2} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold">CRITICAL · Going Concern</span>
              </div>
              <p className="text-sm font-bold leading-tight mb-2">Auditor flagged material uncertainty on company's ability to continue operations</p>
              <p className="text-xs text-zinc-700 leading-relaxed">Note 47 of the FY24 report mentions deferred payments to suppliers exceeding ₹240Cr with no clear repayment schedule. Cash from operations turned negative.</p>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="border border-zinc-300 p-2">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">Health</div>
                <div className="text-lg font-bold text-[#FF2A00]">31</div>
              </div>
              <div className="border border-zinc-300 p-2">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">Flags</div>
                <div className="text-lg font-bold">14</div>
              </div>
              <div className="border border-zinc-300 p-2">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">Verdict</div>
                <div className="text-[10px] font-bold mt-1.5">RISKY</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER MARQUEE */}
      <div className="border-b-2 border-strong bg-black text-white overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-3 text-xs uppercase tracking-[0.3em]">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-12 pr-12">
              <span>► Aggressive Revenue Recognition</span>
              <span className="text-[#FF2A00]">● Going Concern</span>
              <span>► Related-Party Transactions</span>
              <span className="text-[#FF2A00]">● Off-Balance-Sheet Liabilities</span>
              <span>► Inventory Buildup</span>
              <span className="text-[#FF2A00]">● Receivables Aging</span>
              <span>► Auditor Resignation</span>
              <span className="text-[#FF2A00]">● Insider Selling</span>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES */}
      <section className="border-b-2 border-strong">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">/ 01 What it does</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.9]">
                Six layers of forensic scrutiny on every report.
              </h2>
            </div>
            <div className="lg:col-span-7 text-zinc-700 leading-relaxed flex items-end">
              We don't summarize. We interrogate. Each upload triggers a six-section breakdown
              that mirrors what a forensic accountant would spend two weeks doing.
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-strong">
            {[
              { i: FileSearch, t: "Executive Summary", d: "3-5 sentence verdict on company health, sector positioning, and overall trajectory." },
              { i: TrendingDown, t: "Financial Highlights", d: "Top 10 KPIs with YoY deltas and contextual commentary — not just numbers." },
              { i: Layers, t: "Key Ratios", d: "15+ ratios across liquidity, leverage, profitability, efficiency — each benchmarked." },
              { i: Skull, t: "Red Flags", d: "Going concern, aggressive accounting, related-party deals, insider selling — graded by severity." },
              { i: ScanLine, t: "Management Commentary", d: "Tone analysis, buzzword detection, omissions, and a credibility score for the MD&A." },
              { i: AlertTriangle, t: "Minute Details", d: "The fine print: contingent liabilities, auditor remarks, notes to accounts. Where it hides." },
            ].map(({ i: Icon, t, d }, idx) => (
              <div key={idx} className="border border-strong -mt-px -ml-px p-8 bg-white hover:bg-[#F4F4F5] transition-colors">
                <Icon className="w-6 h-6 mb-6" strokeWidth={1.5} />
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">/ {String(idx + 1).padStart(2, "0")}</div>
                <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{t}</h3>
                <p className="text-sm text-zinc-700 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="border-b-2 border-strong bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">/ 02 Pricing</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.9] mb-12 max-w-2xl">
            Free during early access.
          </h2>
          <div className="grid md:grid-cols-2 gap-0 border-2 border-strong">
            <div className="p-10 border-r-0 md:border-r-2 border-strong">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">FREE</div>
              <div className="font-display font-black text-6xl tracking-tighter mb-6">₹0</div>
              <ul className="space-y-3 text-sm">
                <li>✓ 2 full analyses / month</li>
                <li>✓ PDF + ticker fetch</li>
                <li>✓ Deep forensic engine</li>
                <li>✓ Saved history</li>
              </ul>
              <Link to="/auth?mode=signup" className="inline-flex items-center gap-2 mt-8 bg-black text-white px-6 py-3 text-sm uppercase tracking-[0.2em] hover:bg-[#FF2A00] transition-colors" data-testid="pricing-cta">
                Start Free <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="p-10 bg-[#F4F4F5] text-zinc-700">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">WHAT'S NEXT</div>
              <p className="text-sm leading-relaxed">
                FinLens is in early access — every account gets a monthly allowance of full analyses,
                no card required. As more people use it, we'll grow that allowance and roll out
                paid tiers for heavier usage. Nothing changes on your existing reports either way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-12 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Crosshair className="w-4 h-4" />
            <span className="font-display font-bold">FINLENS</span>
            <span className="text-xs text-zinc-500 ml-2">© 2026 · Forensic Financial Analysis</span>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
            Not investment advice · Educational use only
          </div>
        </div>
      </footer>
    </div>
  );
}
